import { create } from 'zustand';
import type { User, Team, LoginRequest, RegisterRequest } from '../types';
import { authService } from '../features/auth/services/authService';

interface AuthStore {
  // 状态
  user: User | null;
  team: Team | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  
  // 操作
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => void;
  initialize: () => void;
  clearError: () => void;
  updateUser: (updatedUser: User) => void;
  
  // 计算属性
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  // 初始状态
  user: null,
  team: null, 
  token: null,
  isLoading: false,
  error: null,

  // 登录
  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login(credentials);

      // 存储到localStorage
      authService.storeAuthData(response);

      // 更新状态
      set({
        user: response.user,
        team: response.team,
        token: response.access_token,
        isLoading: false,
        error: null
      });

      // 验证并清理工作空间状态
      const { useWorkspaceStore } = await import('./workspaceStore');
      await useWorkspaceStore.getState().validateAndCleanWorkspace();
    } catch (error: any) {
      set({
        isLoading: false,
        error: error.response?.data?.message || '登录失败'
      });
      throw error;
    }
  },

  // 注册
  register: async (data: RegisterRequest) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.register(data);

      // 存储到localStorage
      authService.storeAuthData(response);

      // 更新状态
      set({
        user: response.user,
        team: response.team,
        token: response.access_token,
        isLoading: false,
        error: null
      });

      // 验证并清理工作空间状态
      const { useWorkspaceStore } = await import('./workspaceStore');
      await useWorkspaceStore.getState().validateAndCleanWorkspace();

    } catch (error: any) {
      set({
        isLoading: false,
        error: error.response?.data?.message || '注册失败'
      });
      throw error;
    }
  },

  // 退出登录
  logout: () => {
    authService.logout();
    set({
      user: null,
      team: null,
      token: null,
      error: null
    });
  },

  // 初始化（从localStorage恢复状态）
  initialize: () => {
    const user = authService.getStoredUser();
    const team = authService.getStoredTeam();
    const token = authService.getStoredToken();


    if (user && token) {  // 移除了对team的必需检查
      set({ user, team, token });

      // 延迟验证工作空间状态，避免初始化冲突
      setTimeout(async () => {
        try {
          const { useWorkspaceStore } = await import('./workspaceStore');
          await useWorkspaceStore.getState().validateAndCleanWorkspace();
        } catch (error) {
        }
      }, 100);  // 增加延迟时间
    } else {
    }
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },

  // 更新用户信息
  updateUser: (updatedUser: User) => {
    set(state => ({ 
      user: { ...state.user, ...updatedUser }
    }));
    
    // 同步到localStorage
    const currentUser = get().user;
    if (currentUser) {
      authService.updateStoredUser(currentUser);
    }
  },

  // 检查是否已认证
  isAuthenticated: () => {
    const { user, token } = get();
    return !!(user && token);
  },

  // 检查是否为管理员
  isAdmin: () => {
    const { user } = get();
    return user?.isAdmin === true;
  }
}));